import { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader/PageHeader';
import CustomTabPanel from '../../components/Tab/CustomTabPanel';
import SummaryInvoices from '../../components/ElectronicBilling/SummaryInvoices';
import SentInvoicesTable from '../../components/ElectronicBilling/SentInvoicesTable';
import ReceivedInvoicesTable from '../../components/ElectronicBilling/ReceivedInvoicesTable';
import TransactionTable from '../../components/TransactionsTable/TransactionTable';
import ConfiguredRutState from '../../components/ElectronicBilling/ConfiguredRutState';
import { exportAccountingExcel } from '../../services/AccountingExportService';
import MonthYearSelector from '../../components/Reports/Sales/MonthYearSelector';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { getIcon } from '../../utils/IconHandler';
import useConfiguredBillingRuts from '../../hooks/useConfiguredBillingRuts';
import dayjs from 'dayjs';
import './AccountingPage.scss';

const ACCOUNTING_TABS = [
  { key: 'summary', label: 'Resumen mensual' },
  { key: 'issued', label: 'Facturas emitidas' },
  { key: 'received', label: 'Facturas recibidas' },
  { key: 'transactions', label: 'Transacciones' },
];

const AccountingPage = () => {
  const { t } = useTranslation();
  const [rutTab, setRutTab] = useState(0);
  const [sectionTab, setSectionTab] = useState(0);
  const [exportMonth, setExportMonth] = useState(dayjs().month() + 1);
  const [exportYear, setExportYear] = useState(dayjs().year());
  const [exporting, setExporting] = useState(false);
  const { ruts, loading: loadingRuts, error: rutsError, reload } = useConfiguredBillingRuts();
  const showBillingConfigurationState = loadingRuts || rutsError || ruts.length === 0;

  const currentRut = ruts[rutTab]?.rut ?? null;
  const currentProvider = ruts[rutTab]?.provider ?? null;

  const handleExportExcel = async () => {
    if (!currentRut) return;

    setExporting(true);
    try {
      const result = await exportAccountingExcel({
        rut: currentRut,
        month: exportMonth,
        year: exportYear,
      });
      SnackbarUtils.success(
        `Excel descargado: ${result.issuedCount} emitidas y ${result.receivedCount} recibidas.`
      );
    } catch (error) {
      console.error('Error exporting accounting excel', error);
      SnackbarUtils.error('No se pudo descargar el Excel contable.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box className={`accounting-page${showBillingConfigurationState ? ' accounting-page--configuration-state' : ''}`}>
      {loadingRuts ? <ConfiguredRutState loading /> : rutsError || ruts.length === 0 ? (
        <ConfiguredRutState error={rutsError} onRetry={reload} />
      ) : (
        <>
          <PageHeader
            title="Contabilidad"
            subtitle="Cierre mensual, facturación y conciliación para contadores"
          />

          <Stack className="accounting-stack" spacing={2}>
          <Paper className="accounting-toolbar" elevation={0}>
            <Box className="accounting-toolbar__header">
              <Box>
                <Typography className="accounting-toolbar__eyebrow">Empresa fiscal</Typography>
                <Typography className="accounting-toolbar__rut">{currentRut}</Typography>
              </Box>
              <Box className="accounting-export">
                <Box className="accounting-export__selector">
                  <MonthYearSelector
                    month={exportMonth}
                    year={exportYear}
                    setMonth={setExportMonth}
                    setYear={setExportYear}
                  />
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : getIcon('download')}
                  onClick={handleExportExcel}
                  disabled={exporting || !currentRut}
                >
                  {exporting ? 'Preparando...' : 'Descargar Excel'}
                </Button>
              </Box>
            </Box>

            <Box className="accounting-rut-list" role="tablist" aria-label="RUTs contables">
              {ruts.map((rutData, index) => (
                <ButtonBase
                  key={rutData.rut}
                  className={`accounting-rut-chip ${rutTab === index ? 'is-active' : ''}`}
                  onClick={() => {
                    setRutTab(index);
                    setSectionTab(0);
                  }}
                  role="tab"
                  aria-selected={rutTab === index}
                >
                  <span>{rutData.rut}</span>
                </ButtonBase>
              ))}
            </Box>

            <Box className="accounting-section-tabs">
              <Tabs
                value={sectionTab}
                onChange={(_, value) => setSectionTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Secciones contables"
              >
                {ACCOUNTING_TABS.map((tab) => (
                  <Tab key={tab.key} label={tab.label} />
                ))}
              </Tabs>
            </Box>
          </Paper>

          <CustomTabPanel value={sectionTab} index={0} boxSx={{ p: 0 }}>
            <SummaryInvoices selectedRut={currentRut} embedded />
          </CustomTabPanel>
          <CustomTabPanel value={sectionTab} index={1} boxSx={{ p: 0 }}>
            <SentInvoicesTable selectedRut={currentRut} provider={currentProvider} embedded />
          </CustomTabPanel>
          <CustomTabPanel value={sectionTab} index={2} boxSx={{ p: 0 }}>
            <ReceivedInvoicesTable selectedRut={currentRut} selectedLocationId={ruts[rutTab]?.locationId} embedded />
          </CustomTabPanel>
          <CustomTabPanel value={sectionTab} index={3} boxSx={{ p: 0 }}>
            <Paper className="accounting-content-panel" elevation={0}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t('transactions') || 'Transacciones'}
              </Typography>
              <TransactionTable showCancelFor="none" preferredRut={currentRut} enableAccountingFallback />
            </Paper>
          </CustomTabPanel>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default AccountingPage;
